'use client';

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useToastModal, ToastType } from '@/contexts/ToastModalContext';
import styles from './ToastModal.module.css';
import { useTranslations } from 'next-intl';

export interface ToastModalProps {
  isVisible: boolean;
  type: ToastType;
  message: string;
  onClose: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

const ToastModal: React.FC = () => {
  const { state, hideToast } = useToastModal();

  if (!state.isVisible || typeof window === 'undefined') {
    return null;
  }

  return createPortal(
    <ToastModalComponent
      isVisible={state.isVisible}
      type={state.type}
      message={state.message}
      onClose={hideToast}
      autoClose={false}
      autoCloseDelay={0}
    />,
    document.body
  ) as React.ReactElement;
};

const ToastModalComponent: React.FC<ToastModalProps> = ({
  isVisible,
  type,
  message,
  onClose,
  autoClose = true,
  autoCloseDelay = 5000
}) => {
  const t = useTranslations('toast');

  useEffect(() => {
    if (isVisible && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [isVisible, autoClose, autoCloseDelay, onClose]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisible) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isVisible, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'error':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 16V12M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={styles.overlay}
      data-testid="toast-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="toast-modal-message">
      <div
        className={`${styles.modal} ${styles[`modal--${type}`]}`}
        data-testid="toast-modal-content"
        onClick={(e) => e.stopPropagation()}>
        <div className={styles.content}>
          <p className={styles.message} data-testid="toast-modal-message" id="toast-modal-message">
            {message}
          </p>

          <div className={styles.iconContainer} data-testid="toast-modal-icon">
            {getIcon()}
          </div>
        </div>

        <button
          className={styles.closeButton}
          data-testid="toast-modal-close"
          onClick={onClose}
          aria-label={t('close')}
          type="button">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ToastModal;
